import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { getProductsByCollections, createOrder } from '../../state/actions/order';

import StripePayment from './stripe_payment';
import '../../styles/shopify-shop.scss';

/* ── helpers ─────────────────────────────────────── */

const ALLOWED_LABELS = ['Size', 'Color', ''];
const MAX_OPTIONS = 3;
const TAX_RATE = 0.25;

/** Pick the first allowed option_label for a product (max 1). */
const pickOptionLabel = (labels = []) => labels.find((l) => ALLOWED_LABELS.includes(l)) ?? null;

/** Derive the display options from the variant titles – max 3. */
const deriveOptions = (variants = []) =>
  variants.slice(0, MAX_OPTIONS).map((v) => ({
    variantId: v.id,
    label: v.title,
    image: v.image?.src || v.image?.url || null,
  }));

/** Resolve the best image to display for a product given a selected variant. */
const resolveImage = (product, selectedVariantId) => {
  if (selectedVariantId) {
    const variant = (product.variants || []).find((v) => v.id === selectedVariantId);
    const variantImg = variant?.image?.src || variant?.image?.url || null;
    if (variantImg) return variantImg;
  }
  return product.images?.[0]?.src || product.images?.[0]?.url || null;
};

/** Get total available across all variants. */
const totalStock = (variants = []) =>
  variants.reduce((sum, v) => sum + Math.max(0, v.total_available ?? 0), 0);

/** Cheapest price among variants. */
const displayPrice = (variants = []) => {
  if (!variants.length) return null;
  const amounts = variants.map((v) => parseFloat(v.price?.amount ?? 0));
  return Math.min(...amounts);
};

/* ── placeholder SVG when no image ── */
const PlaceholderIcon = () => (
  <svg
    className="shopify-shop__placeholder-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 16l5-5 4 4 3-3 6 6" />
    <circle cx="15.5" cy="8.5" r="1.5" />
  </svg>
);

/* ── product card ────────────────────────────────── */

function ProductCard({
  product,
  cartItem,
  onSelect,
  onDeselect,
  onQtyChange,
  onOptionChange,
  onImageClick,
}) {
  const { t } = useTranslation();
  const optionLabel = pickOptionLabel(product.option_labels);
  const options = optionLabel ? deriveOptions(product.variants) : [];
  const stock = totalStock(product.variants);

  // Show the selected variant's price when one is chosen, otherwise the cheapest
  const price = (() => {
    if (cartItem?.selectedVariantId) {
      const variant = product.variants.find((v) => v.id === cartItem.selectedVariantId);
      if (variant) return parseFloat(variant.price?.amount ?? 0);
    }
    return displayPrice(product.variants);
  })();

  // Resolve the displayed image – switches when a variant with its own image is selected
  const currentImage = resolveImage(product, cartItem?.selectedVariantId);

  const isSelected = !!cartItem;

  return (
    <div className="shopify-shop__card">
      {/* image / placeholder – click to view full size */}
      <div
        className={`shopify-shop__image-wrapper${
          currentImage ? ' shopify-shop__image-wrapper--clickable' : ''
        }`}
        onClick={() => currentImage && onImageClick(currentImage, product.title)}
      >
        {currentImage ? <img src={currentImage} alt={product.title} /> : <PlaceholderIcon />}
      </div>

      {/* title */}
      <span className="shopify-shop__title">{product.title}</span>

      {/* price & stock */}
      {price !== null && (
        <span className="shopify-shop__price">
          {price.toLocaleString('sv-SE', { minimumFractionDigits: 2 })} kr
        </span>
      )}
      {stock > 0 && (
        <span className="shopify-shop__stock">
          {t('shop_stock_status', 'Lager status')} {stock} st
        </span>
      )}

      {/* select / qty button */}
      {!isSelected ? (
        <button className="shopify-shop__btn" onClick={() => onSelect(product)}>
          {t('shop_select', 'Välj')}
        </button>
      ) : (
        <>
          <div className="shopify-shop__btn shopify-shop__btn--selected">
            <button className="shopify-shop__qty-btn" onClick={() => onQtyChange(product.id, -1)}>
              –
            </button>
            <span>
              ({t('shop_selected', 'Vald')}) {cartItem.qty}
            </span>
            <button className="shopify-shop__qty-btn" onClick={() => onQtyChange(product.id, 1)}>
              +
            </button>
          </div>

          {/* option pills */}
          {options.length > 0 && (
            <div className="shopify-shop__options">
              {options.map((opt) => (
                <button
                  key={opt.variantId}
                  className={`shopify-shop__option-pill${
                    cartItem.selectedVariantId === opt.variantId
                      ? ' shopify-shop__option-pill--active'
                      : ''
                  }`}
                  onClick={() => onOptionChange(product.id, opt.variantId)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {/* remove */}
          <button className="shopify-shop__btn--remove" onClick={() => onDeselect(product.id)}>
            {t('shop_remove', 'Tabort')} 🗑
          </button>
        </>
      )}
    </div>
  );
}

/* ── image lightbox ──────────────────────────────── */

function Lightbox({ src, alt, onClose }) {
  if (!src) return null;

  return (
    <div className="shopify-shop__lightbox" onClick={onClose}>
      <button className="shopify-shop__lightbox-close" onClick={onClose}>
        &times;
      </button>
      <img
        className="shopify-shop__lightbox-img"
        src={src}
        alt={alt || ''}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

/* ── main shop component ─────────────────────────── */

function ShopifyShop({ collectionIds = [], bookingContext = {} }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { products: rawProducts, loading, error, createOrderLoading, paymentLink } = useSelector(
    (state) => state.order
  );

  // Filter out variants with total_available <= 0, then drop products with no remaining variants
  const products = useMemo(() => {
    if (!rawProducts) return [];
    return rawProducts
      .map((p) => ({
        ...p,
        variants: (p.variants || []).filter((v) => (v.total_available ?? 0) > 0),
      }))
      .filter((p) => p.variants.length > 0);
  }, [rawProducts]);

  // cart: { [productId]: { qty, selectedVariantId, product } }
  const [cart, setCart] = useState({});

  // lightbox state
  const [lightbox, setLightbox] = useState({ src: null, alt: '' });

  const handleImageClick = useCallback((src, alt) => {
    setLightbox({ src, alt });
  }, []);

  const handleLightboxClose = useCallback(() => {
    setLightbox({ src: null, alt: '' });
  }, []);

  useEffect(() => {
    if (collectionIds.length) {
      dispatch(
        getProductsByCollections({
          page: 1,
          perPage: 20,
          collections: collectionIds,
          optionAs: ['Color', 'Size', '__empty__'],
          userId: bookingContext.userId || null,
        })
      );
    }
  }, [dispatch, collectionIds, bookingContext.userId]);

  /* ── cart helpers ── */

  const handleSelect = useCallback((product) => {
    const defaultVariant = product.variants?.[0];
    setCart((prev) => ({
      ...prev,
      [product.id]: {
        qty: 1,
        selectedVariantId: defaultVariant?.id ?? null,
        product,
      },
    }));
  }, []);

  const handleDeselect = useCallback((productId) => {
    setCart((prev) => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });
  }, []);

  const handleQtyChange = useCallback((productId, delta) => {
    setCart((prev) => {
      const item = prev[productId];
      if (!item) return prev;
      const newQty = item.qty + delta;
      if (newQty <= 0) {
        const next = { ...prev };
        delete next[productId];
        return next;
      }
      return { ...prev, [productId]: { ...item, qty: newQty } };
    });
  }, []);

  const handleOptionChange = useCallback((productId, variantId) => {
    setCart((prev) => {
      const item = prev[productId];
      if (!item) return prev;
      return { ...prev, [productId]: { ...item, selectedVariantId: variantId } };
    });
  }, []);

  /* ── derived totals ── */

  const { totalAmount, totalItems } = useMemo(() => {
    let amount = 0;
    let items = 0;
    Object.values(cart).forEach(({ qty, selectedVariantId, product }) => {
      const variant =
        product.variants.find((v) => v.id === selectedVariantId) || product.variants[0];
      if (variant) {
        amount += parseFloat(variant.price?.amount ?? 0) * qty;
      }
      items += qty;
    });
    return { totalAmount: amount, totalItems: items };
  }, [cart]);

  const vatAmount = totalAmount - totalAmount / (1 + TAX_RATE);

  /* ── confirm / create order ── */

  const handleConfirm = useCallback(() => {
    const orderRows = Object.values(cart).map(({ qty, selectedVariantId, product }) => {
      const variant =
        product.variants.find((v) => v.id === selectedVariantId) || product.variants[0];
      const priceIncVat = parseFloat(variant?.price?.amount ?? 0);
      const priceExVat = priceIncVat / (1 + TAX_RATE);

      return {
        shopifyProductVariantId: variant?.id ?? selectedVariantId,
        name: product.title,
        description: variant?.title ?? '',
        unitPrice: priceExVat,
        currencyId: 1,
        unit: 'fixed',
        quantity: qty,
        type: 'part',
        vat: 25,
        discount: 0,
        isApprovedByCustomer: true,
      };
    });

    dispatch(
      createOrder({
        buyingUserId: bookingContext.buyingUserId,
        buyingUserEmail: bookingContext.buyingUserEmail,
        statusId: 1,
        serviceId: bookingContext.serviceId,
        eventBookingId: bookingContext.eventBookingId,
        successUrl: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/payment-cancel?session_id={CHECKOUT_SESSION_ID}`,
        orderRows,
      })
    );
  }, [cart, bookingContext, dispatch]);

  /* ── render ── */

  if (loading) {
    return null; // parent already has a spinner when needed
  }

  if (error) {
    return null; // fail silently – the shop is supplementary
  }

  if (!products || products.length === 0) {
    return null;
  }

  /* ── show Stripe payment iframe after successful order ── */
  if (paymentLink) {
    return (
      <div className="shopify-shop">
        <StripePayment paymentLink={paymentLink} />
      </div>
    );
  }

  return (
    <div className="shopify-shop">
      <p className="shopify-shop__heading">
        {t('shop_heading', 'Ta gärna del av våra erbjudanden')}
      </p>
      <p className="shopify-shop__subheading">
        <strong>
          {bookingContext.eventBookingId
            ? t('shop_subheading_event', 'Vi levererar till eventplatsen.')
            : t('shop_subheading', 'Köp nu redo i butik!')}
        </strong>
      </p>

      <div className="shopify-shop__grid">
        {products.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            cartItem={cart[product.id] || null}
            onSelect={handleSelect}
            onDeselect={handleDeselect}
            onQtyChange={handleQtyChange}
            onOptionChange={handleOptionChange}
            onImageClick={handleImageClick}
          />
        ))}
      </div>

      {/* full-size image lightbox */}
      <Lightbox src={lightbox.src} alt={lightbox.alt} onClose={handleLightboxClose} />

      {/* cart summary – only shown when items selected */}
      {totalItems > 0 && (
        <div className="shopify-shop__summary">
          <p className="shopify-shop__summary-line">
            {t('shop_total', 'Belopp')}:{' '}
            {totalAmount.toLocaleString('sv-SE', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{' '}
            kr
          </p>
          <p className="shopify-shop__summary-line">
            {t('shop_vat', 'Varav moms')} (25%){' '}
            {vatAmount.toLocaleString('sv-SE', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{' '}
            Kr
          </p>
          <p className="shopify-shop__summary-line">
            {t('shop_item_count', 'Antal valda artiklar')}: {totalItems} st
          </p>

          <button
            className="shopify-shop__confirm-btn"
            onClick={handleConfirm}
            disabled={createOrderLoading}
          >
            {createOrderLoading
              ? t('shop_confirming', 'Skickar...')
              : t('shop_confirm', 'Bekräfta')}
          </button>
        </div>
      )}
    </div>
  );
}

export default ShopifyShop;
